import React, { useState, useEffect } from 'react';
import { HiOutlineUser, HiOutlineMail, HiOutlinePhone, HiOutlineOfficeBuilding, HiOutlineUserGroup, HiOutlinePencilAlt } from 'react-icons/hi';
import './Participation.css';
import { participationService } from '../services/participationService';
import { conferencesService } from '../services/conferencesService';
import { supabase } from '../supabaseClient';
import { visiteursService } from '../services/visiteursService';
import CountdownTimer from './CountdownTimer';
import absImg from '../assets/abs.png';
import confImg from '../assets/conf.png';
import abstractPdf from '../assets/ABSTRACT-TEMPLATE.pdf';
import conferencePdf from '../assets/Conference-Template.pdf';

function Participation() {
  const [participationData, setParticipationData] = useState({
    participationHeader: {
      title: 'Participation au Congrès',
      subtitle: ''
    },
    eventInfo: {
      title: 'Informations sur l\'Événement',
      congressTitle: '',
      congressDates: '',
      location: '',
      subLocation: ''
    },
    countdownInfo: {
      title: 'Compte à Rebours',
      targetDate: '',
      targetTime: '09:00',
      isActive: true
    },
    formInfo: {
      title: 'Formulaire d\'Inscription',
      submitButtonText: 'S\'inscrire',
      registrationOpen: true
    },
    additionalInfo: {
      title: 'Informations Importantes',
      notes: []
    }
  });

  const [availableConferences, setAvailableConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    organisation: '',
    paye: '',
    ville: '',
    profil: '',
    categorie: '',
    conference: '',
    informations: '',
    typeParticipation: '', // default: no selection
    modeParticipation: '',
    mot_cle: '',
    axe: '',
    piece_jointe: null,
    photo_profil: null,
    langue_preference: '',
    jour_visiteur: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popup, setPopup] = useState({ show: false, message: '', error: false });
  const [focusedInput, setFocusedInput] = useState('');
  const [fullConferenceIds, setFullConferenceIds] = useState([]);

  useEffect(() => {
    if (participationData.formInfo) {
      const showPart = participationData.formInfo.showParticipantForm !== false;
      const showConf = participationData.formInfo.showConferencierForm !== false;

      if (showPart && !showConf && formData.typeParticipation !== 'participant') {
        setFormData(prev => ({ ...prev, typeParticipation: 'participant' }));
      } else if (!showPart && showConf && formData.typeParticipation !== 'conferencier_principal') {
        setFormData(prev => ({ ...prev, typeParticipation: 'conferencier_principal' }));
      }
    }
  }, [participationData.formInfo, formData.typeParticipation]);

  useEffect(() => {
    // Scroll to top when participation page is accessed directly
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadParticipationData();
    loadAvailableConferences();
    checkFullConferences();
  }, []);

  const loadParticipationData = async () => {
    try {
      setLoading(true);
      console.log('Loading participation data...');

      // Get active edition first
      const { data: activeEdition, error: editionError } = await supabase
        .from('editions')
        .select('year')
        .eq('is_active', true)
        .single();

      let editionYear = '2025'; // Default fallback

      if (editionError) {
        console.warn('No active edition found, using fallback:', editionYear);
      } else if (activeEdition) {
        editionYear = activeEdition.year;
        console.log('Active edition found:', editionYear);
      }

      const data = await participationService.getParticipationData(editionYear);
      console.log('Participation service returned:', data);

      if (data) {
        setParticipationData(data);
        console.log('Participation data set:', data);
      } else {
        // Use default data if none found
        console.warn('No participation data found, using defaults');
      }
      setError(null);
    } catch (error) {
      console.error('Error loading participation data:', error);
      setError('Failed to load participation data');
    } finally {
      setLoading(false);
      console.log('Participation loading completed');
    }
  };

  const loadAvailableConferences = async () => {
    try {
      // Get active edition first
      const { data: activeEdition } = await supabase
        .from('editions')
        .select('year')
        .eq('is_active', true)
        .single();

      let editionYear = '2025'; // Default fallback
      if (activeEdition) {
        editionYear = activeEdition.year;
      }

      const data = await conferencesService.getConferencesData(editionYear);

      if (data && data.conferences) {
        // Filter only confirmed conferences
        const confirmedConferences = data.conferences.filter(conf => conf.statut === 'confirmee');
        setAvailableConferences(confirmedConferences);
      }
    } catch (error) {
      console.error('Error loading conferences:', error);
      setAvailableConferences([]);
    }
  };

  // Check which conferences are full
  const checkFullConferences = async () => {
    try {
      // Get active edition
      const { data: activeEdition } = await supabase
        .from('editions')
        .select('year')
        .eq('is_active', true)
        .single();
      let editionYear = '2025';
      if (activeEdition) editionYear = activeEdition.year;

      // Get all visiteurs for this edition
      const visiteurs = await visiteursService.getVisiteurs(editionYear);

      // Get conferences data
      const confData = await conferencesService.getConferencesData(editionYear);
      const confs = confData?.conferences || [];
      const salles = confData?.salles || [];

      // Map salleId to capacity
      const salleCapacity = {};
      salles.forEach(salle => {
        salleCapacity[salle.id] = salle.capacite || 0;
      });

      // Count confirmed participants per conference
      const confCounts = {};
      visiteurs.forEach(v => {
        if (v.statut === 'email confirme' && v.conference) {
          confCounts[v.conference] = (confCounts[v.conference] || 0) + 1;
        }
      });

      // Find full conferences
      const fullIds = [];
      confs.forEach(conf => {
        const salleId = conf.salleId || conf.salle_id;
        const capacity = salleCapacity[salleId] || 0;
        const count = confCounts[conf.id] || 0;
        if (capacity > 0 && count >= capacity) {
          fullIds.push(conf.id.toString());
        }
      });
      setFullConferenceIds(fullIds);
    } catch (e) {
      setFullConferenceIds([]);
    }
  };

  // Use target date from data
  const targetDate = participationData.countdownInfo.targetDate && participationData.countdownInfo.targetTime
    ? new Date(`${participationData.countdownInfo.targetDate}T${participationData.countdownInfo.targetTime}`)
    : new Date();

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };

  const handleInputFocus = (e) => {
    setFocusedInput(e.target.name);
  };

  const handleInputBlur = () => {
    setFocusedInput('');
  };

  const handleJourToggle = (dayOption) => {
    let selected = formData.jour_visiteur ? formData.jour_visiteur.split(', ').filter(Boolean) : [];
    if (selected.includes(dayOption)) {
      selected = selected.filter(d => d !== dayOption);
    } else {
      selected = [...selected, dayOption];
    }
    setFormData(prev => ({ ...prev, jour_visiteur: selected.join(', ') }));
    if (formErrors.jour_visiteur) {
      setFormErrors(prev => ({ ...prev, jour_visiteur: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.nom.trim()) errors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) errors.prenom = 'Le prénom est requis';
    if (!formData.email.trim()) errors.email = 'L\'email est requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'L\'email n\'est pas valide';
    if (!formData.telephone.trim()) errors.telephone = 'Le téléphone est requis';
    if (!formData.paye.trim()) errors.paye = 'Le pays est requis';
    if (!formData.ville.trim()) errors.ville = 'La ville est requise';
    if (formData.typeParticipation !== 'conferencier_principal' && !formData.categorie) errors.categorie = 'La catégorie est requise';
    if (!formData.profil) errors.profil = 'Le profil est requis';
    if (formData.typeParticipation === 'conferencier_principal' && !formData.modeParticipation) errors.modeParticipation = 'Le mode de participation est requis';
    if (formData.typeParticipation === 'participant') {
      if (!formData.jour_visiteur) errors.jour_visiteur = 'Veuillez sélectionner au moins un jour';
    } else if (formData.typeParticipation === 'conferencier_principal') {
      if (!formData.mot_cle.trim()) errors.mot_cle = 'Les mots clés sont requis';
      if (!formData.axe.trim()) errors.axe = 'L\'axe est requis';
      if (!formData.langue_preference) errors.langue_preference = 'La préférence de langue est requise';
      // piece_jointe is optional
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSubmitting(true);
    try {
      // Get active edition year
      const { data: activeEdition, error: editionError } = await supabase
        .from('editions')
        .select('year')
        .eq('is_active', true)
        .single();
      let editionYear = '2025';
      if (activeEdition) editionYear = activeEdition.year;
      // Prepare data for API
      let submitData = { ...formData };
      // If file is present, handle upload (implement upload logic as needed)
      if (formData.piece_jointe) {
        try {
          const fileUrl = await visiteursService.uploadPieceJointe(formData.piece_jointe);
          submitData.piece_jointe_url = fileUrl;
        } catch (uploadError) {
          throw new Error('Erreur lors de l\'envoi du fichier. Vous n\'avez peut-être pas créé le bucket "visiteurs_files" dans Supabase.');
        }
      }
      // Remove file object from submitData
      delete submitData.piece_jointe;

      if (formData.photo_profil) {
        try {
          const photoUrl = await visiteursService.uploadPhotoProfil(formData.photo_profil);
          submitData.photo_profil_url = photoUrl;
        } catch (uploadPhotoError) {
          throw new Error('Erreur lors de l\'envoi de la photo de profil.');
        }
      }
      delete submitData.photo_profil;
      // If participant, keep conference, else remove
      if (formData.typeParticipation !== 'participant') {
        submitData.conference = null;
      }
      await visiteursService.addVisiteur(submitData, editionYear);
      setPopup({ show: true, message: 'Inscription réussie !', error: false });
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        organisation: '',
        paye: '',
        ville: '',
        profil: '',
        categorie: '',
        conference: '',
        informations: '',
        typeParticipation: 'participant',
        modeParticipation: '',
        mot_cle: '',
        axe: '',
        piece_jointe: null,
        photo_profil: null,
        langue_preference: '',
        jour_visiteur: '',
      });
      setFormErrors({});
      await checkFullConferences();
    } catch (err) {
      setPopup({ show: true, message: err.message || 'Erreur lors de l\'inscription', error: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="participation-page">
        <div className="participation-container">
          <div className="participation-header">
            {/* Replace text with animated loading spinner */}
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (

    <div className="participation-page">
      {/* Popup responsive */}
      {popup.show && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              background: '#fff',

              padding: '32px 24px',
              maxWidth: 320,
              width: '90%',
              boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 600, color: popup.error ? '#002b5b' : '#1f882eff', marginBottom: 12 }}>
              {popup.error ? 'Erreur' : 'Succès'}
            </div>
            <div style={{ fontSize: 16, marginBottom: 20, whiteSpace: 'pre-line' }}>{popup.message}</div>
            <button
              style={{
                padding: '8px 24px',

                border: 'none',
                background: popup.error ? '#002b5b' : '#1f882eff',
                color: '#fff',
                fontWeight: 500,
                fontSize: 16,
                cursor: 'pointer'
              }}
              onClick={() => setPopup({ show: false, message: '', error: false })}
            >
              OK
            </button>
          </div>
        </div>
      )}
      <div className="participation-container">
        <div className="participation-header">
          <h1>{participationData.participationHeader.title}</h1>
          <p>{participationData.participationHeader.subtitle}</p>
        </div>

        <div className="participation-content">
          <div className="participation-info">
            <div className="event-info">
              <h2>{participationData.eventInfo.title}</h2>
              <div className="event-details">
                <h3>{participationData.eventInfo.congressTitle}</h3>
                <p className="event-dates">{participationData.eventInfo.congressDates}</p>
                <p className="event-location">
                  {participationData.eventInfo.location}
                  <br />
                  {participationData.eventInfo.subLocation}
                </p>
              </div>
            </div>

            {participationData.countdownInfo.isActive && (
              <div className="countdown-section">
                <h2>{participationData.countdownInfo.title}</h2>
                <CountdownTimer targetDate={targetDate} isActive={participationData.countdownInfo.isActive} />
              </div>
            )}

            <div className="additional-info">
              <h3>{participationData.additionalInfo.title}</h3>
              <ul className="important-notes">
                {participationData.additionalInfo.notes.map((note, index) => (
                  <li key={index}>{note}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="participation-form">
            <h2>{participationData.formInfo.title}</h2>

            {!participationData.formInfo.registrationOpen ? (
              <div className="registration-closed">
                <div className="closed-icon">✕</div>
                <h3>Les inscriptions sont terminées</h3>
                <p>Notre rendez-vous est au congrès !</p>
                <p className="closed-details">
                  Nous avons hâte de vous accueillir lors de l'événement.
                  Si vous avez des questions, n'hésitez pas à nous contacter.
                </p>
              </div>
            ) : (
              <>
                <div className="form-contact-info">
                  <p>Si vous rencontrez des problèmes lors de votre inscription ou avez des questions concernant votre participation, n'hésitez pas à nous contacter.</p>
                </div>
                <form onSubmit={handleSubmit}>
                  {/* Type de participation selector always shown first */}
                  <div className="form-group" style={{ display: (participationData.formInfo.showParticipantForm !== false && participationData.formInfo.showConferencierForm !== false) ? 'block' : 'none' }}>
                    <label style={{ fontWeight: 500, fontSize: 18, marginBottom: 12, display: 'block' }}>Type de participation *</label>
                    <div className="participation-buttons" style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                      {participationData.formInfo.showParticipantForm !== false && (
                        <button
                          type="button"
                          className={formData.typeParticipation === "participant" ? "active participation-btn" : "participation-btn"}
                          style={{
                            padding: '12px 24px',

                            border: formData.typeParticipation === "participant" ? '2px solid #002b5b' : '2px solid #e0e0e0',
                            background: formData.typeParticipation === "participant" ? '#f7e6e6' : '#fff',
                            color: formData.typeParticipation === "participant" ? '#002b5b' : '#333',
                            fontWeight: 500,
                            fontSize: 16,
                            cursor: 'pointer',
                            boxShadow: formData.typeParticipation === "participant" ? '0 2px 8px rgba(31,136,46,0.08)' : 'none',
                            transition: 'all 0.2s',
                            outline: 'none',
                            minWidth: 180
                          }}
                          onClick={() => setFormData(prev => ({ ...prev, typeParticipation: "participant", categorie: prev.categorie || "visiteur" }))}
                        >
                          Assister au congrès / Participer
                        </button>
                      )}
                      {participationData.formInfo.showConferencierForm !== false && (
                        <button
                          type="button"
                          className={formData.typeParticipation === "conferencier_principal" ? "active participation-btn" : "participation-btn"}
                          style={{
                            padding: '12px 24px',

                            border: formData.typeParticipation === "conferencier_principal" ? '2px solid #002b5b' : '2px solid #e0e0e0',
                            background: formData.typeParticipation === "conferencier_principal" ? '#f7e6e6' : '#fff',
                            color: formData.typeParticipation === "conferencier_principal" ? '#002b5b' : '#333',
                            fontWeight: 500,
                            fontSize: 16,
                            cursor: 'pointer',
                            boxShadow: formData.typeParticipation === "conferencier_principal" ? '0 2px 8px rgba(31,136,46,0.08)' : 'none',
                            transition: 'all 0.2s',
                            outline: 'none',
                            minWidth: 180
                          }}
                          onClick={() => setFormData({ ...formData, typeParticipation: "conferencier_principal", categorie: "", jour_visiteur: "" })}
                        >
                          Conférencier principal
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Only show the rest of the form if a type is chosen */}
                  {formData.typeParticipation ? (
                    <>
                      {formData.typeParticipation === 'conferencier_principal' && (
                        <div className="form-group" style={{ marginBottom: '30px' }}>
                          <label style={{ fontWeight: 500, fontSize: 18, marginBottom: 12, display: 'block' }}>Mode de participation *</label>
                        <div className="participation-buttons" style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                          <button
                            type="button"
                            className={formData.modeParticipation === "presentiel" ? "active participation-btn" : "participation-btn"}
                            style={{
                              padding: '12px 24px',
                              border: formData.modeParticipation === "presentiel" ? '2px solid #002b5b' : '2px solid #e0e0e0',
                              background: formData.modeParticipation === "presentiel" ? '#f7e6e6' : '#fff',
                              color: formData.modeParticipation === "presentiel" ? '#002b5b' : '#333',
                              fontWeight: 500,
                              fontSize: 16,
                              cursor: 'pointer',
                              boxShadow: formData.modeParticipation === "presentiel" ? '0 2px 8px rgba(31,136,46,0.08)' : 'none',
                              transition: 'all 0.2s',
                              outline: 'none',
                              minWidth: 180,
                              flex: 1
                            }}
                            onClick={() => setFormData({ ...formData, modeParticipation: "presentiel" })}
                          >
                            Présentiel
                          </button>
                          <button
                            type="button"
                            className={formData.modeParticipation === "online" ? "active participation-btn" : "participation-btn"}
                            style={{
                              padding: '12px 24px',
                              border: formData.modeParticipation === "online" ? '2px solid #002b5b' : '2px solid #e0e0e0',
                              background: formData.modeParticipation === "online" ? '#f7e6e6' : '#fff',
                              color: formData.modeParticipation === "online" ? '#002b5b' : '#333',
                              fontWeight: 500,
                              fontSize: 16,
                              cursor: 'pointer',
                              boxShadow: formData.modeParticipation === "online" ? '0 2px 8px rgba(31,136,46,0.08)' : 'none',
                              transition: 'all 0.2s',
                              outline: 'none',
                              minWidth: 180,
                              flex: 1
                            }}
                            onClick={() => setFormData({ ...formData, modeParticipation: "online" })}
                          >
                            En ligne
                          </button>
                        </div>
                        {formErrors.modeParticipation && <span className="error-message">{formErrors.modeParticipation}</span>}
                      </div>
                      )}

{formData.typeParticipation === "conferencier_principal" && (
                             <div className="templates-section" style={{ display: 'flex', gap: '20px', marginBottom: '30px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <div className="template-card" style={{ width: '210px', height: '297px', border: '1px solid #e0e0e0', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundImage: `url(${absImg})`, backgroundSize: 'cover', backgroundPosition: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                          <a href={abstractPdf} download="ABSTRACT-TEMPLATE.pdf" className="download-btn" style={{ padding: '8px 16px', background: '#002b5b', color: 'white', textDecoration: 'none',  fontSize: '14px', fontWeight: '500', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                            Télécharger
                          </a>
                        </div>
                        <div className="template-card" style={{ width: '210px', height: '297px', border: '1px solid #e0e0e0', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundImage: `url(${confImg})`, backgroundSize: 'cover', backgroundPosition: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                          <a href={conferencePdf} download="Conference-Template.pdf" className="download-btn" style={{ padding: '8px 16px', background: '#002b5b', color: 'white', textDecoration: 'none',  fontSize: '14px', fontWeight: '500', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                            Télécharger
                          </a>
                        </div>
                      </div>
)
}
                      {formData.typeParticipation === 'conferencier_principal' && (
                        <div className="form-group" style={{ marginBottom: '30px' }}>
                          <label style={{ fontWeight: 500, fontSize: 18, marginBottom: 12, display: 'block' }}>Préférence de langue pour communiquer *</label>
                          <div className="participation-buttons" style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                            <button
                              type="button"
                              className={formData.langue_preference === "francais" ? "active participation-btn" : "participation-btn"}
                              style={{
                                padding: '12px 24px',
                                border: formData.langue_preference === "francais" ? '2px solid #002b5b' : '2px solid #e0e0e0',
                                background: formData.langue_preference === "francais" ? '#f7e6e6' : '#fff',
                                color: formData.langue_preference === "francais" ? '#002b5b' : '#333',
                                fontWeight: 500,
                                fontSize: 16,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none',
                                flex: 1
                              }}
                              onClick={() => setFormData({ ...formData, langue_preference: "francais" })}
                            >
                              Français
                            </button>
                            <button
                              type="button"
                              className={formData.langue_preference === "anglais" ? "active participation-btn" : "participation-btn"}
                              style={{
                                padding: '12px 24px',
                                border: formData.langue_preference === "anglais" ? '2px solid #002b5b' : '2px solid #e0e0e0',
                                background: formData.langue_preference === "anglais" ? '#f7e6e6' : '#fff',
                                color: formData.langue_preference === "anglais" ? '#002b5b' : '#333',
                                fontWeight: 500,
                                fontSize: 16,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none',
                                flex: 1
                              }}
                              onClick={() => setFormData({ ...formData, langue_preference: "anglais" })}
                            >
                              Anglais
                            </button>
                            <button
                              type="button"
                              className={formData.langue_preference === "arabe" ? "active participation-btn" : "participation-btn"}
                              style={{
                                padding: '12px 24px',
                                border: formData.langue_preference === "arabe" ? '2px solid #002b5b' : '2px solid #e0e0e0',
                                background: formData.langue_preference === "arabe" ? '#f7e6e6' : '#fff',
                                color: formData.langue_preference === "arabe" ? '#002b5b' : '#333',
                                fontWeight: 500,
                                fontSize: 16,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none',
                                flex: 1
                              }}
                              onClick={() => setFormData({ ...formData, langue_preference: "arabe" })}
                            >
                              Arabe
                            </button>
                          </div>
                          {formErrors.langue_preference && <span className="error-message">{formErrors.langue_preference}</span>}
                        </div>
                      )}
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="nom">Nom *</label>
                          <div className="input-with-icon">
                            {focusedInput !== 'nom' && !formData.nom && <HiOutlineUser className="input-icon" />}
                            <input
                              type="text"
                              id="nom"
                              name="nom"
                              value={formData.nom}
                              onChange={handleInputChange}
                              onFocus={handleInputFocus}
                              onBlur={handleInputBlur}
                              className={formErrors.nom ? 'error' : ''}
                              required
                            />
                          </div>
                          {formErrors.nom && <span className="error-message">{formErrors.nom}</span>}
                        </div>

                        <div className="form-group">
                          <label htmlFor="prenom">Prénom *</label>
                          <div className="input-with-icon">
                            {focusedInput !== 'prenom' && !formData.prenom && <HiOutlineUser className="input-icon" />}
                            <input
                              type="text"
                              id="prenom"
                              name="prenom"
                              value={formData.prenom}
                              onChange={handleInputChange}
                              onFocus={handleInputFocus}
                              onBlur={handleInputBlur}
                              className={formErrors.prenom ? 'error' : ''}
                              required
                            />
                          </div>
                          {formErrors.prenom && <span className="error-message">{formErrors.prenom}</span>}
                        </div>
                      </div>
                      {/* ...existing form fields... */}
                    </>
                  ) : null}

                  {formData.typeParticipation && <>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <div className="input-with-icon">
                        {focusedInput !== 'email' && !formData.email && <HiOutlineMail className="input-icon" />}
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                          className={formErrors.email ? 'error' : ''}
                          required
                        />
                      </div>
                      <small style={{ color: '#666', fontSize: '12px', marginTop: '0px', marginBottom: '10px', display: 'block' }}>Veuillez entrer une adresse e-mail valide pour recevoir la confirmation.</small>
                      {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="telephone">Téléphone *</label>
                      <div className="input-with-icon">
                        {focusedInput !== 'telephone' && !formData.telephone && <HiOutlinePhone className="input-icon" />}
                        <input
                          type="tel"
                          id="telephone"
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleInputChange}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                          className={formErrors.telephone ? 'error' : ''}
                          required
                        />
                      </div>
                      {formErrors.telephone && <span className="error-message">{formErrors.telephone}</span>}
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="paye">Pays *</label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="paye"
                            name="paye"
                            value={formData.paye}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            className={formErrors.paye ? 'error' : ''}
                            required
                          />
                        </div>
                        {formErrors.paye && <span className="error-message">{formErrors.paye}</span>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="ville">Ville *</label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="ville"
                            name="ville"
                            value={formData.ville}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            className={formErrors.ville ? 'error' : ''}
                            required
                          />
                        </div>
                        {formErrors.ville && <span className="error-message">{formErrors.ville}</span>}
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="organisation">Organisation</label>
                      <div className="input-with-icon">
                        {focusedInput !== 'organisation' && !formData.organisation && <HiOutlineOfficeBuilding className="input-icon" />}
                        <input
                          type="text"
                          id="organisation"
                          name="organisation"
                          value={formData.organisation}
                          onChange={handleInputChange}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="profil">Profil *  (Sélectionnez votre profil)</label>
                      <div className="input-with-icon">
                        {focusedInput !== 'profil' && !formData.profil && <HiOutlineUserGroup className="input-icon" />}
                        <select
                          id="profil"
                          name="profil"
                          value={formData.profil}
                          onChange={handleInputChange}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                          className={formErrors.profil ? 'error' : ''}
                          required
                        >
                          <option value=""></option>
                          <option value="student">Doctorant</option>
                          <option value="professor">Professeur</option>
                          <option value="professional">Professionnel</option>
                          <option value="Expert Security Governance">Expert Security Governance</option>
                        </select>
                      </div>
                      {formErrors.profil && <span className="error-message">{formErrors.profil}</span>}
                    </div>
                    {formData.typeParticipation !== 'conferencier_principal' && (
                      <div className="form-group">
                        <label htmlFor="categorie">Catégorie *</label>
                        <div className="input-with-icon">
                          <select
                            id="categorie"
                            name="categorie"
                            value={formData.categorie}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            className={formErrors.categorie ? 'error' : ''}
                            required
                          >
                            <option value=""></option>
                            <option value="participant">Participant</option>
                            <option value="conferencier">Conférencier</option>
                            <option value="visiteur">Visiteur</option>
                          </select>
                        </div>
                        {formErrors.categorie && <span className="error-message">{formErrors.categorie}</span>}
                      </div>
                    )}
                    {formData.typeParticipation === 'participant' && (
                      <div className="form-group" style={{ marginBottom: '25px', marginTop: '15px' }}>
                        <label style={{ fontWeight: 600, fontSize: 16, marginBottom: 12, display: 'block', color: '#002b5b' }}>
                          Sélectionner par jour *
                        </label>
                        <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '10px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
                            <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                              <tr>
                                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#1e293b', fontSize: 14, width: '70px', textAlign: 'center' }}>Sélection</th>
                                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#1e293b', fontSize: 14, width: '220px' }}>Jour et date</th>
                                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#1e293b', fontSize: 14 }}>Panels du programme</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[
                                {
                                  id: 'Lundi 2 novembre 2026',
                                  day: 'Jour 1',
                                  dateFr: 'Lundi 2 Novembre 2026',
                                  dateEn: 'Mon. 2 Nov. 2026',
                                  panels: [
                                    { tag: 'Panel 1', text: '“SSG in Africa, role of international & regional organizations”' },
                                    { tag: 'Panel 2', text: '“Hybridity and private security in fragile and conflict-affected settings in Africa”' },
                                    { tag: 'Panel 3', text: '«La montée en puissance des acteurs non étatiques et des capacités complémentaires de sécurité : sécurité privée, gestion des crises et résilience»' }
                                  ]
                                },
                                {
                                  id: 'Mardi 3 novembre 2026',
                                  day: 'Jour 2',
                                  dateFr: 'Mardi 3 Novembre 2026',
                                  dateEn: 'Tuesday 3 Nov. 2026',
                                  panels: [
                                    { tag: 'Panel 4', text: '“People-Centred Security, Democratic Governance and Sustainable Peace in Fragile Contexts”' },
                                    { tag: 'Panel 5', text: '“Rule of Law, Public Accountability, Strategic Communication and Institutional Resilience”' },
                                    { tag: 'Panel 6', text: '“Artificial Intelligence, Digital Governance and Public Security: Building Responsible, Secure and Accountable Institutions”' }
                                  ]
                                },
                                {
                                  id: 'Mercredi 4 novembre 2026',
                                  day: 'Jour 3',
                                  dateFr: 'Mercredi 4 Novembre 2026',
                                  dateEn: 'Wednesday 4 Nov. 2026',
                                  panels: [
                                    { tag: 'Panel 7', text: '“Institutional Capacity Development, Organisational Transformation and Learning Systems”' },
                                    { tag: 'Panel 8', text: '“Digital Sovereignty, Cyber Resilience and Responsible Innovation in Public Security”' }
                                  ]
                                }
                              ].map((dayOption, index) => {
                                const selectedDays = formData.jour_visiteur ? formData.jour_visiteur.split(', ') : [];
                                const isSelected = selectedDays.includes(dayOption.id);
                                return (
                                  <tr 
                                    key={dayOption.id}
                                    onClick={() => handleJourToggle(dayOption.id)}
                                    style={{ 
                                      borderBottom: index !== 2 ? '1px solid #e2e8f0' : 'none', 
                                      background: isSelected ? '#f0f6ff' : '#fff',
                                      cursor: 'pointer',
                                      transition: 'background 0.2s'
                                    }}
                                  >
                                    <td style={{ padding: '16px', verticalAlign: 'top', textAlign: 'center' }}>
                                      <div style={{
                                        width: 22, height: 22, margin: '4px auto 0',
                                        border: isSelected ? '2px solid #002b5b' : '2px solid #cbd5e1',
                                        background: isSelected ? '#002b5b' : '#fff',
                                        color: '#fff',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 14, fontWeight: 'bold', borderRadius: '4px',
                                        transition: 'all 0.2s ease'
                                      }}>
                                        {isSelected ? '✓' : ''}
                                      </div>
                                    </td>
                                    <td style={{ padding: '16px', verticalAlign: 'top' }}>
                                      <div style={{ fontWeight: 700, color: '#002b5b', fontSize: 15, marginBottom: '2px' }}>{dayOption.day}</div>
                                      <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{dayOption.dateFr}</div>
                                      <div style={{ fontSize: 12, color: '#64748b', marginTop: '2px' }}>{dayOption.dateEn}</div>
                                    </td>
                                    <td style={{ padding: '16px', verticalAlign: 'top' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {dayOption.panels.map((p, pIdx) => (
                                          <div 
                                            key={pIdx} 
                                            style={{ 
                                              display: 'flex', 
                                              alignItems: 'baseline', 
                                              gap: '8px', 
                                              background: isSelected ? '#ffffff' : '#f8fafc',
                                              padding: '6px 10px',
                                              borderRadius: '6px',
                                              border: '1px solid',
                                              borderColor: isSelected ? '#bfdbfe' : '#e2e8f0'
                                            }}
                                          >
                                            <span style={{ 
                                              background: isSelected ? '#002b5b' : '#475569', 
                                              color: '#fff', 
                                              fontWeight: 600, 
                                              padding: '2px 8px', 
                                              borderRadius: '4px', 
                                              fontSize: 11,
                                              letterSpacing: '0.3px',
                                              whiteSpace: 'nowrap',
                                              flexShrink: 0
                                            }}>
                                              {p.tag}
                                            </span>
                                            <span style={{ fontSize: 13, color: '#334155', lineHeight: 1.45, fontWeight: 500 }}>
                                              {p.text}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {formErrors.jour_visiteur && (
                          <span className="error-message" style={{ display: 'block', marginTop: '8px', color: '#e53e3e', fontWeight: 500 }}>
                            {formErrors.jour_visiteur}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="form-group">
                      <label htmlFor="informations">TITRE DE COMMUNICATION </label>
                      <div className="input-with-icon">
                        <textarea
                          id="informations"
                          name="informations"
                          rows="4"
                          value={formData.informations}
                          onChange={handleInputChange}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                        ></textarea>
                      </div>
                    </div>
                  </>}
                  {formData.typeParticipation === 'participant' && formData.categorie !== 'visiteur' && (
                    <div className="form-group">
                      <label>Choisir la conférence *</label>
                      <div className={`conference-list ${formErrors.conference ? 'error' : ''}`}>
                        {availableConferences.length === 0 ? (
                          <div className="no-conferences">
                            <p>Aucune conférence disponible pour le moment.</p>
                          </div>
                        ) : (
                          availableConferences.map((conf) => {
                            const isFull = fullConferenceIds.includes(conf.id.toString());
                            return (
                              <div key={conf.id} className="conference-row">
                                <label className="conference-label" style={isFull ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                                  <input
                                    type="radio"
                                    name="conference"
                                    value={conf.id}
                                    checked={formData.conference === conf.id.toString()}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isFull}
                                  />
                                  <div className="conference-card">
                                    <span className="conference-title">{conf.titre}</span>
                                    <span className="conference-dates">
                                      {conf.dateDebut} {conf.heureDebut} - {conf.dateFin} {conf.heureFin}
                                    </span>
                                    {isFull && (
                                      <span style={{ color: '#002b5b', fontWeight: 500, marginLeft: 8 }}>
                                        Participation insuffisante pour cette conférence
                                      </span>
                                    )}
                                  </div>
                                </label>
                              </div>
                            );
                          })
                        )}
                      </div>
                      {formErrors.conference && <span className="error-message">{formErrors.conference}</span>}
                    </div>

                  )}
                  {formData.typeParticipation === 'conferencier_principal' && (
                    <>
               
                      <div className="form-group">
                        <label htmlFor="mot_cle">Mots clés * </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="mot_cle"
                            name="mot_cle"
                            value={formData.mot_cle}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            className={formErrors.mot_cle ? 'error' : ''}
                            required
                          />
                        </div>
                        {formErrors.mot_cle && <span className="error-message">{formErrors.mot_cle}</span>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="axe">Axe *</label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="axe"
                            name="axe"
                            value={formData.axe}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            className={formErrors.axe ? 'error' : ''}
                            required
                          />
                        </div>
                        {formErrors.axe && <span className="error-message">{formErrors.axe}</span>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="piece_jointe">Pièce jointe (proposition de la communication/ article)</label>
                        <div className="file-upload-wrapper">
                          <input
                            type="file"
                            id="piece_jointe"
                            name="piece_jointe"
                            accept=".pdf,.doc,.docx,.jpg,.png"
                            onChange={handleInputChange}
                            className="file-upload-input"
                          />
                          <label htmlFor="piece_jointe" className="file-upload-button">
                            <svg xmlns="http://www.w3.org/2000/svg" className="upload-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            Sélectionner un fichier
                          </label>
                         <span className="file-upload-name">
  {formData.piece_jointe ? (
    <span key="has-file">{formData.piece_jointe.name}</span>
  ) : (
    <span key="no-file">
      Le nom du fichier doit obligatoirement commencer
      <br />
      par Nom_Prenom_Titres de communication du participant
    </span>
  )}
</span>
                        </div>
                      </div>

                      <div className="form-group" style={{ marginTop: '20px' }}>
                        <label htmlFor="photo_profil">Photo de profil : (facultatif )</label>
                        <div className="file-upload-wrapper">
                          <input
                            type="file"
                            id="photo_profil"
                            name="photo_profil"
                            accept=".jpg,.jpeg,.png"
                            onChange={handleInputChange}
                            className="file-upload-input"
                          />
                          <label htmlFor="photo_profil" className="file-upload-button">
                            <svg xmlns="http://www.w3.org/2000/svg" className="upload-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            Sélectionner une photo
                          </label>
                          <span className="file-upload-name">
                             {formData.photo_profil ? <span key="has-photo">{formData.photo_profil.name}</span> : <span key="no-photo">Aucune photo sélectionnée (Max: 5MB)</span>}
                          </span>
                        </div>
                      </div>


                    </>
                  )}



                  <button
                    type="submit"
                    className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                    disabled={isSubmitting  || !formData.typeParticipation}
                    style={!formData.typeParticipation ? { background: '#ccc', color: '#888', cursor: 'not-allowed' } : {}}
                  >
                    {isSubmitting ? 'Inscription en cours...' : participationData.formInfo.submitButtonText}
                  </button>
                </form>

                {formData.typeParticipation === 'conferencier_principal' && (
                  <div className="payment-plans-section" style={{ marginTop: '50px', borderTop: '1px solid #eee', paddingTop: '40px' }}>
                    <h3 style={{ textAlign: 'center', marginBottom: '25px', color: '#333', fontSize: '20px', fontWeight: '600' }}>Frais de participation</h3>
                    
                    <h4 style={{ textAlign: 'center', marginBottom: '15px', color: '#002b5b', fontSize: '16px' }}>Participation en présentiel</h4>
                    <div className="payment-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px', alignItems: 'stretch' }}>
                      {(participationData.paymentPlans?.presentiel || []).map((plan, idx) => (
                        <div key={plan.id || idx} className="payment-card" style={{ background: '#002b5b', border: '1px solid #002b5b',  padding: '15px 10px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'transform 0.3s', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                          <h5 style={{ fontSize: '13px', color: '#fff', marginBottom: '8px', fontWeight: '600' }}>{plan.title}</h5>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fff', marginBottom: '8px' }}>{plan.price}</div>
                          <p style={{ color: '#ffeaea', fontSize: '11px', margin: 0 }}>{plan.description}</p>
                        </div>
                      ))}
                    </div>

                    <h4 style={{ textAlign: 'center', marginBottom: '15px', color: '#002b5b', fontSize: '16px' }}>Participation en ligne</h4>
                    <div className="payment-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', alignItems: 'stretch' }}>
                      {(participationData.paymentPlans?.online || []).map((plan, idx) => (
                        <div key={plan.id || idx} className="payment-card" style={{ background: '#fff', border: '1px solid #e0e0e0',  padding: '15px 10px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'transform 0.3s', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                          <h5 style={{ fontSize: '13px', color: '#555', marginBottom: '8px', fontWeight: '600' }}>{plan.title}</h5>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#002b5b', marginBottom: '8px' }}>{plan.price}</div>
                          <p style={{ color: '#666', fontSize: '11px', margin: 0 }}>{plan.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Participation;



